import { useEffect, useState } from "react";
import { api, espnImg } from "@/lib/api";
import { Target, TrendingUp, ShieldCheck } from "lucide-react";

const rankSuffix = (n) => {
  if (n == null) return "";
  const s = ["th", "st", "nd", "rd"];
  const v = Number(n) % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

/**
 * Opponent Scout — compact strip. Renders the Titans' next opponent's record,
 * top-3 leaders, and offensive/defensive rank. Silent no-render if no
 * upcoming game or ESPN scout data is unavailable.
 */
export default function OpponentScout({ eventId }) {
  const [d, setD] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const params = eventId ? { event_id: eventId } : {};
        const { data } = await api.get("/opponent-scout", { params });
        setD(data);
      } catch { setD(null); }
    })();
  }, [eventId]);

  if (!d || !d.active || !d.opponent) return null;
  const opp = d.opponent;
  const hasRanks = d.offense_rank || d.defense_rank;
  const hasLeaders = (d.leaders || []).length > 0;
  if (!opp.record && !hasRanks && !hasLeaders) return null;

  return (
    <div
      className="mt-4 pt-4 border-t border-white/20"
      data-testid="opponent-scout"
    >
      <div className="flex items-center gap-2 mb-2">
        <Target size={12} className="text-white/80" />
        <span className="font-cond uppercase tracking-[0.25em] text-[10px] text-white/85">
          Scouting {opp.name || opp.abbr}
        </span>
        {opp.record && (
          <span className="ml-auto bg-white/15 text-white font-display uppercase text-xs px-1.5 py-0.5 tabular-nums" data-testid="opponent-record">
            {opp.record}
          </span>
        )}
      </div>

      {hasRanks && (
        <div className="grid grid-cols-2 gap-2 mb-2" data-testid="opponent-ranks">
          <div className="bg-black/25 px-2 py-1.5 flex items-center gap-2">
            <TrendingUp size={12} className="text-titans-blue" />
            <span className="font-cond uppercase tracking-wider text-[10px] text-white/70">Offense</span>
            <span className="ml-auto font-display text-white text-sm tabular-nums">
              {d.offense_rank ? rankSuffix(d.offense_rank) : "—"}
            </span>
          </div>
          <div className="bg-black/25 px-2 py-1.5 flex items-center gap-2">
            <ShieldCheck size={12} className="text-titans-red" />
            <span className="font-cond uppercase tracking-wider text-[10px] text-white/70">Defense</span>
            <span className="ml-auto font-display text-white text-sm tabular-nums">
              {d.defense_rank ? rankSuffix(d.defense_rank) : "—"}
            </span>
          </div>
        </div>
      )}

      {hasLeaders && (
        <div className="flex gap-2 -mx-1 overflow-x-auto pb-1" data-testid="opponent-leaders">
          {d.leaders.map((p, i) => (
            <div
              key={p.id || i}
              data-testid={`opponent-leader-${i}`}
              className="flex-1 min-w-[90px] bg-black/25 px-2 py-1.5 flex items-center gap-2"
            >
              {p.headshot ? (
                <img src={espnImg(p.headshot, 64)} alt={p.name} loading="lazy" className="w-7 h-7 object-cover object-top border border-white/25 bg-black/40" />
              ) : (
                <div className="w-7 h-7 flex items-center justify-center border border-white/25 bg-black/40 text-white font-display text-[10px]">
                  {p.position || "—"}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="font-display text-white text-xs uppercase truncate leading-tight" title={p.name}>{p.name}</div>
                <div className="font-cond uppercase tracking-wider text-[9px] text-white/60 truncate">
                  {p.category} {p.displayValue ? `· ${p.displayValue}` : ""}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
